import React, { useContext, useEffect, useReducer, useState } from 'react';
import api from '../api';
import { useNavigate, useLocation } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';
import { Store } from '../Store';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import { getError } from '../utils';
import { designsWithThumbnails } from '../utils/savedDesigns';
import Card from 'react-bootstrap/Card';
import Pagination from '../components/general/Pagination';
import {
  Container,
  Form,
  Nav,
  Badge,
  DropdownButton,
  Dropdown,
  Modal,
} from 'react-bootstrap';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' }; // a kept error would outlive the next successful fetch (the render checks it after loading)
    case 'FETCH_SUCCESS':
      return {
        ...state,
        designs: action.payload.designs,
        page: action.payload.page,
        pages: action.payload.pages,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    case 'DELETE_REQUEST':
      return { ...state, loadingDelete: true, successDelete: false };
    case 'DELETE_SUCCESS':
      return {
        ...state,
        loadingDelete: false,
        successDelete: true,
      };
    case 'DELETE_FAIL':
      return { ...state, loadingDelete: false, successDelete: false };
    case 'DELETE_RESET':
      return { ...state, loadingDelete: false, successDelete: false };

    default:
      return state;
  }
};

export default function DesignListScreen() {
  const [
    { loading, error, designs, pages, loadingDelete, successDelete },
    dispatch,
  ] = useReducer(reducer, {
    loading: true,
    error: '',
    designs: [],
  });

  const navigate = useNavigate();

  const { state } = useContext(Store);
  const { userInfo } = state; // gets the userInfo out of the application state, so that the users token can be used to authorize the fetch request

  const [pendingDesigns, setPendingDesigns] = useState(null);
  const [countVersion, setCountVersion] = useState(0); // bumped after every count fetch; the list effect follows it
  const [refreshKey, setRefreshKey] = useState(0); // State to trigger refresh

  const { search } = useLocation(); // gets the query parameters of the URL and saves them in the variable called 'search'
  const sp = new URLSearchParams(search); // creates a new instance of the URLSearchParams class, passing in the search string, wich allows to apply several methods to work with the query parameters
  const page = sp.get('page') || 1; // Retrieves the value of the 'page' query parameter (the number on wich page the user is on)
  const searchQuery = sp.get('search') || ''; // Retrieves the 'search' parameter from the URL query string; defaults to an empty string if not present
  const filterParam =
    sp.get('filter') || (pendingDesigns > 0 ? 'pending' : 'all'); // the tab from the URL, which the Nav navigates to; without one, the pending tab while designs wait for review. Derived, not state: a state set once by the count effect missed every later tab click (fixed Oct 2026)

  const [searchInput, setSearchInput] = useState(searchQuery); // Local state for search input

  const [show, setShow] = useState(false);
  const [selectedThumbnail, setSelectedThumbnail] = useState(''); // Store the thumbnail URL
  const [layoutMode, setLayoutMode] = useState(3);
  const [screenSize, setScreenSize] = useState('lg'); // Track current screen size

  useEffect(() => {
    const updateScreenSize = () => {
      if (window.matchMedia('(max-width: 576px)').matches) {
        setScreenSize('xs'); // Extra Small
      } else if (window.matchMedia('(max-width: 768px)').matches) {
        setScreenSize('sm'); // Small
      } else if (window.matchMedia('(max-width: 992px)').matches) {
        setScreenSize('md'); // Medium
      } else {
        setScreenSize('lg'); // Large
      }
    };

    updateScreenSize(); // Set initial size
    window.addEventListener('resize', updateScreenSize); // Update on resize
    return () => window.removeEventListener('resize', updateScreenSize); // Cleanup
  }, []); // Determine screen size dynamically

  useEffect(() => {
    if (screenSize === 'xs' && layoutMode !== 1) {
      setLayoutMode(1);
    } else if (screenSize === 'sm' && layoutMode > 2) {
      setLayoutMode(2);
    } else if (screenSize === 'md' && layoutMode > 3) {
      setLayoutMode(3);
    }
  }, [screenSize, layoutMode]); // Reset layout mode if it's invalid for the current screen size

  const getColClasses = () => {
    if (filterParam === 'pending') {
      return 'col-12'; // Always return 'col-12' for the 'pending' filter
    }

    switch (layoutMode) {
      case 1:
        return 'col-12';
      case 2:
        return 'col-sm-6';
      case 3:
        return 'col-md-4';
      case 4:
        return 'col-lg-3';
      default:
        return 'col-lg-4 col-md-6 col-sm-12';
    }
  }; // Generate column classes based on layout mode

  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const { data } = await api.get('/api/designs/count/documents');
        setPendingDesigns(data.count);
      } catch (error) {
        setPendingDesigns(0); // the list fetch waits for the count: without this a failed count left the overlay up for good (fixed Oct 2026)
        toast.error(getError(error));
      } finally {
        setCountVersion((version) => version + 1); // the list fetches once per refresh, after the count that picks its tab; following refreshKey directly it fetched twice, and the two could land out of order (fixed Oct 2026)
      }
    };

    fetchPendingCount();
  }, [refreshKey]); // Run only when refresh is triggered

  useEffect(() => {
    if (pendingDesigns === null) return; // Ensure we wait for pending count to load

    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });

      try {
        // Include the search parameter in the API request if it exists
        const { data } = await api.get(
          `/api/designs?page=${page}&search=${encodeURIComponent(
            searchQuery
          )}&filter=${filterParam}` // encodeURIComponent: encodes the searchQuery to ensure that special characters are properly formatted in the URL, preventing errors and ensuring accurate query handling
        );

        // Check if there are designs before requesting signed URLs
        if (data.designs.length === 0) {
          dispatch({
            type: 'FETCH_SUCCESS',
            payload: { ...data, designs: [] }, // Just return empty designs list
          });
          return;
        }

        // Fetch signed URLs for thumbnails
        const designIds = data.designs.map((design) => design._id);
        const { data: signedUrls } = await api.post('/api/designs/thumbnails', {
          designIds,
        });

        // Merge signed URLs into design metadata
        const updatedDesigns = designsWithThumbnails(
          data.designs,
          signedUrls.thumbnails
        ); // '' for a design the route left out (utils/savedDesigns.js; the stored link until chain 10, Oct 2026: user decision)

        dispatch({
          type: 'FETCH_SUCCESS',
          payload: { ...data, designs: updatedDesigns },
        });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    }; // Fetches designs based on the current page and search query

    if (successDelete) {
      dispatch({ type: 'DELETE_RESET' });
    } else {
      fetchData();
    }
  }, [
    page,
    userInfo,
    searchQuery,
    filterParam,
    successDelete,
    countVersion,
    pendingDesigns,
  ]);

  const submitHandler = (e) => {
    e.preventDefault();
    const sanitizedInput = searchInput.trim(); // Keep spaces between words but remove leading/trailing spaces
    navigate(
      sanitizedInput
        ? `/admin/designs?filter=${filterParam}&search=${encodeURIComponent(
            sanitizedInput
          )}&page=1`
        : `/admin/designs?filter=${filterParam}&page=1`
    ); // Navigates to the DesignListScreen with the search query and resets to page 1 if sanitizedInput exists; otherwise, navigates to page 1 without any search filter
  };

  const deleteHandler = async (design) => {
    if (
      design.metadata.numOfLikes > 0 ||
      design.metadata.numOfSales > 0 ||
      window.confirm('Do you really want to permanently delete this design?') // only ask the user if he is sure, when the design will be permanently deleted (when numOfSales and numOfLikes are 0)
    ) {
      try {
        dispatch({ type: 'DELETE_REQUEST' });
        await api.delete(`/api/designs/admin/${design._id}`); // sends a delete request for the selected design to the backend
        toast.success('Design deleted.');
        dispatch({ type: 'DELETE_SUCCESS' });
      } catch (err) {
        toast.error(getError(err));
        dispatch({
          type: 'DELETE_FAIL',
        });
      }
    }
  };

  const rejectHandler = async (design) => {
    try {
      await api.put(`/api/designs/admin/${design._id}`, {
        publicStatus: 'rejected',
      });
      toast.success('Design has been rejected.');
      setRefreshKey((prevKey) => prevKey + 1); // Increment refreshKey to trigger useEffect
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const publishHandler = async (design) => {
    try {
      await api.put(`/api/designs/admin/${design._id}`, {
        publicStatus: 'public',
      });
      toast.success('Design has been published.');
      setRefreshKey((prevKey) => prevKey + 1); // Increment refreshKey to trigger useEffect
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const resetPublicStatus = async (design) => {
    if (
      window.confirm(
        'Are you sure you want to reset the public status of this design?'
      )
    ) {
      try {
        await api.put(
          `/api/designs/admin/reset-public-status/${design._id}`,
          {}
        );
        toast.success('Public status has been reset.');
        setRefreshKey((prevKey) => prevKey + 1); // Trigger re-fetch to update UI
      } catch (err) {
        toast.error(getError(err));
      }
    }
  };

  const undeleteHandler = async (design) => {
    if (window.confirm('Are you sure you want to restore this design?')) {
      try {
        await api.put(`/api/designs/admin/undelete/${design._id}`, {});
        toast.success('Design has been restored successfully.');
        setRefreshKey((prevKey) => prevKey + 1); // Trigger re-fetch to update UI
      } catch (err) {
        toast.error(getError(err));
      }
    }
  };

  const handleShow = (thumbnail) => {
    setSelectedThumbnail(thumbnail); // Update selected thumbnail
    setShow(true); // Show modal
  };
  const handleClose = () => setShow(false);

  return (
    <Container>
      <div>
        <h1 className="screen-heading">Designs</h1>

        <Nav
          justify
          variant="tabs"
          activeKey={filterParam}
          onSelect={(newFilterKey) => {
            setSearchInput('');
            navigate(`/admin/designs?filter=${newFilterKey}&page=1`);
          }}
          className="customizer-nav"
        >
          <Nav.Item>
            <Nav.Link eventKey="all">All</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="public">Public</Nav.Link>
          </Nav.Item>
          <Nav.Item style={{ position: 'relative' }}>
            {pendingDesigns > 0 ? (
              <Badge className="notification-badge-nav">{pendingDesigns}</Badge>
            ) : null}
            <Nav.Link eventKey="pending">Pending</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="rejected">Rejected</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="deleted">Deleted</Nav.Link>
          </Nav.Item>
        </Nav>

        {/* Search Form */}
        <Form onSubmit={submitHandler} className="d-flex my-3">
          <Form.Control
            type="text"
            placeholder="Search by name, userId or _id"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search by name, userId or _id"
          />
          <Button
            type="submit"
            variant="secondary"
            style={{ marginLeft: '10px' }}
          >
            Search
          </Button>
        </Form>

        {filterParam !== 'pending' && (
          <Row className="mb-4">
            <Col md={8}></Col>

            <Col
              md={4}
              className="d-flex justify-content-end align-items-center"
            >
              {/* Layout Selector */}
              {screenSize !== 'xs' && (
                <Button
                  key={1}
                  variant={layoutMode === 1 ? 'dark' : 'light'}
                  className="me-2"
                  onClick={() => setLayoutMode(1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="10"
                    height="10"
                    fill="currentColor"
                    className="bi bi-grid"
                    viewBox="0 0 5 5"
                  >
                    <rect
                      key={'1'}
                      x={0.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                  </svg>
                </Button>
              )}
              {screenSize !== 'xs' && (
                <Button
                  key={2}
                  variant={layoutMode === 2 ? 'dark' : 'light'}
                  className={screenSize === 'sm' ? '' : 'me-2'}
                  onClick={() => setLayoutMode(2)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="10"
                    fill="currentColor"
                    className="bi bi-grid"
                    viewBox="0 0 10 5"
                  >
                    <rect
                      key={'1'}
                      x={0.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      key={'2'}
                      x={5.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                  </svg>
                </Button>
              )}
              {screenSize === 'lg' || screenSize === 'md' ? (
                <Button
                  key={3}
                  variant={layoutMode === 3 ? 'dark' : 'light'}
                  className={screenSize === 'md' ? '' : 'me-2'}
                  onClick={() => setLayoutMode(3)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="30"
                    height="10"
                    fill="currentColor"
                    className="bi bi-grid"
                    viewBox="0 0 15 5"
                  >
                    <rect
                      key={'1'}
                      x={0.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      key={'2'}
                      x={5.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      key={'3'}
                      x={10.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                  </svg>
                </Button>
              ) : null}
              {screenSize === 'lg' && (
                <Button
                  key={4}
                  variant={layoutMode === 4 ? 'dark' : 'light'}
                  className={screenSize === 'lg' ? '' : 'me-2'}
                  onClick={() => setLayoutMode(4)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="10"
                    fill="currentColor"
                    className="bi bi-grid"
                    viewBox="0 0 20 5"
                  >
                    <rect
                      key={'1'}
                      x={0.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      key={'2'}
                      x={5.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      key={'3'}
                      x={10.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      key={'4'}
                      x={15.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                  </svg>
                </Button>
              )}
            </Col>
          </Row>
        )}

        {loading || loadingDelete ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <Row>
            {designs.map((design) => (
              <Col
                key={design._id}
                style={{
                  paddingBottom: filterParam === 'pending' ? '30px' : '',
                }}
                className={getColClasses()}
              >
                <Card className="mb-3">
                  <Card.Body className="text-center">
                    <Row
                      className={filterParam === 'pending' ? 'mb-3' : 'mb-1'}
                    >
                      <Col
                        md={4}
                        style={{
                          marginBottom: '0px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {filterParam !== 'pending' ? (
                          <Card.Text>
                            <span
                              className="no-wrap"
                              style={{
                                margin: '0 5px',
                              }}
                            >
                              {design.metadata.numOfLikes}
                            </span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-heart"
                              viewBox="0 0 16 16"
                            >
                              <path d="m8 2.748-.717-.737C5.6.281 2.514.878 1.4 3.053c-.523 1.023-.641 2.5.314 4.385.92 1.815 2.834 3.989 6.286 6.357 3.452-2.368 5.365-4.542 6.286-6.357.955-1.886.838-3.362.314-4.385C13.486.878 10.4.28 8.717 2.01zM8 15C-7.333 4.868 3.279-3.04 7.824 1.143q.09.083.176.171a3 3 0 0 1 .176-.17C12.72-3.042 23.333 4.867 8 15" />
                            </svg>
                          </Card.Text>
                        ) : null}
                      </Col>
                      <Col
                        md={4}
                        style={{
                          marginBottom: '0px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {filterParam === 'pending' ? (
                          <Card.Text
                            style={{
                              fontSize: '20px',
                            }}
                          >
                            <span className="no-wrap w-100">
                              <Button
                                variant="danger"
                                type="button"
                                onClick={() => rejectHandler(design)}
                              >
                                Reject
                              </Button>
                              &nbsp;
                              <Button
                                variant="success"
                                type="button"
                                onClick={() => publishHandler(design)}
                              >
                                Publish
                              </Button>
                            </span>
                          </Card.Text>
                        ) : (
                          <Card.Text
                            style={{
                              marginBottom: '0px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <span
                              className="no-wrap"
                              style={{
                                margin: '0 5px',
                              }}
                            >
                              {design.metadata.numOfSales}
                            </span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-bag-check-fill"
                              viewBox="0 0 16 16"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10.5 3.5a2.5 2.5 0 0 0-5 0V4h5zm1 0V4H15v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V4h3.5v-.5a3.5 3.5 0 1 1 7 0m-.646 5.354a.5.5 0 0 0-.708-.708L7.5 10.793 6.354 9.646a.5.5 0 1 0-.708.708l1.5 1.5a.5.5 0 0 0 .708 0z"
                              />
                            </svg>
                          </Card.Text>
                        )}
                      </Col>

                      <Col
                        md={4}
                        style={{
                          marginBottom: '0px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'end',
                        }}
                      >
                        <DropdownButton
                          drop="down-centered"
                          variant="none"
                          className="no-css exclude-dropdown-arrow"
                          title={
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              fill="#333333"
                              className="bi bi-three-dots-vertical"
                              viewBox="0 0 16 16"
                            >
                              <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0" />
                            </svg>
                          }
                          id="dropdown-menu-align-right"
                        >
                          {filterParam === 'rejected' && (
                            <Dropdown.Item
                              className="d-flex justify-content-start"
                              eventKey="3"
                              onClick={() => resetPublicStatus(design)}
                            >
                              <div>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="14"
                                  height="14"
                                  fill="currentColor"
                                  className="bi bi-arrow-counterclockwise"
                                  viewBox="0 0 16 16"
                                  style={{ marginRight: '6px' }}
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M8 3a5 5 0 1 1-4.546 2.914.5.5 0 0 0-.908-.417A6 6 0 1 0 8 2z"
                                  />
                                  <path d="M8 4.466V.534a.25.25 0 0 0-.41-.192L5.23 2.308a.25.25 0 0 0 0 .384l2.36 1.966A.25.25 0 0 0 8 4.466" />
                                </svg>
                                Reset Public Status
                              </div>
                            </Dropdown.Item>
                          )}

                          <Dropdown.Item
                            className="d-flex justify-content-between"
                            eventKey="1"
                            onClick={() =>
                              navigate(`/customizer/${design._id}`)
                            }
                          >
                            <div>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                fill="currentColor"
                                className="bi bi-grid"
                                viewBox="0 0 16 16"
                                style={{ marginRight: '6px' }}
                              >
                                <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5z" />
                              </svg>
                              Open in Customizer
                            </div>
                          </Dropdown.Item>

                          <Dropdown.Item
                            className="d-flex justify-content-between"
                            eventKey="2"
                            onClick={() =>
                              navigate(`/admin/user/${design.metadata.userId}`)
                            }
                          >
                            <div>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                fill="currentColor"
                                className="bi bi-person"
                                viewBox="0 0 16 16"
                                style={{ marginRight: '6px' }}
                              >
                                <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4m-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10s-3.516.68-4.168 1.332c-.678.678-.83 1.418-.832 1.664z" />
                              </svg>
                              {design.metadata.userId}
                            </div>
                          </Dropdown.Item>

                          {design.metadata.deleted ? (
                            <Dropdown.Item
                              className="d-flex justify-content-start"
                              eventKey="3"
                              onClick={() => undeleteHandler(design)}
                            >
                              <div>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 56 56"
                                  style={{ marginRight: '6px' }}
                                >
                                  <path
                                    fill="currentColor"
                                    d="m41.148 34.926l3.563 3.562l1.148-24.093h2.344c.961 0 1.734-.797 1.734-1.758s-.773-1.782-1.734-1.782H37.961V7.34c0-3.352-2.274-5.531-5.883-5.531h-8.766c-3.609 0-5.859 2.18-5.859 5.53v3.024h-.82l4.008 4.032h21.468Zm8.18 17.25c.703.703 1.875.703 2.555 0c.68-.727.703-1.852 0-2.555L6.672 4.387c-.703-.703-1.875-.727-2.578 0c-.68.68-.68 1.851 0 2.531Zm-28.125-41.32V7.573c0-1.336.96-2.273 2.39-2.273h8.227c1.43 0 2.414.937 2.414 2.273v3.281ZM16.984 54.19h21.492c2.508 0 4.43-1.265 5.204-3.28l.023-.446l-3.117-3.094l-.024.914c-.07 1.383-1.054 2.39-2.46 2.39h-20.79c-1.336 0-2.343-1.03-2.414-2.39l-1.359-27.96l-3.844-3.844l1.524 32.203c.164 3.375 2.367 5.507 5.765 5.507m12.164-31.31v-2.625c0-.844-.656-1.524-1.43-1.524c-.398 0-.796.211-1.007.54v1.195Zm7.313 7.36l.281-9.962c.024-.89-.562-1.547-1.36-1.547c-.726 0-1.335.68-1.359 1.524l-.21 7.336ZM20.78 46.55c.797 0 1.383-.68 1.36-1.524l-.493-16.593l-2.789-2.813l.54 19.406c.023.891.609 1.524 1.382 1.524m6.938 0c.773 0 1.43-.68 1.43-1.524v-9.14l-2.837-2.79v11.93c0 .844.657 1.524 1.407 1.524m6.96 0c.774 0 1.337-.633 1.36-1.524l.07-2.156l-2.648-2.672l-.14 4.828c-.024.844.562 1.524 1.359 1.524"
                                  ></path>
                                </svg>
                                Undelete
                              </div>
                            </Dropdown.Item>
                          ) : (
                            <Dropdown.Item
                              className="d-flex justify-content-start"
                              eventKey="3"
                              onClick={() => deleteHandler(design)}
                            >
                              <div>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="14"
                                  height="14"
                                  fill="currentColor"
                                  className="bi bi-trash"
                                  viewBox="0 0 16 16"
                                  style={{ marginRight: '6px' }}
                                >
                                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                                  <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                                </svg>
                                {design.metadata.numOfLikes > 0 ||
                                design.metadata.numOfSales > 0
                                  ? 'Soft-Delete'
                                  : 'Permanently Delete'}
                              </div>
                            </Dropdown.Item>
                          )}
                        </DropdownButton>
                      </Col>
                    </Row>

                    {/* while the thumbnail can be enlarged it sits in a button, so the zoom is reachable by keyboard (the button takes its accessible name from the image's alt text): */}
                    {screenSize !== 'xs' &&
                    layoutMode !== 1 &&
                    filterParam !== 'pending' ? (
                      <button
                        type="button"
                        className="a11y-unstyled-button"
                        onClick={() =>
                          handleShow(design.metadata.signedThumbnailUrl)
                        }
                      >
                        <img
                          src={design.metadata.signedThumbnailUrl}
                          alt={design.name}
                          className="img-fluid thumbnail-community-designs-scale"
                        />
                      </button>
                    ) : (
                      <img
                        src={design.metadata.signedThumbnailUrl}
                        alt={design.name}
                        className={
                          layoutMode === 1 || filterParam === 'pending'
                            ? 'img-fluid'
                            : 'img-fluid thumbnail-community-designs-scale'
                        }
                      />
                    )}

                    <div className="divider"></div>

                    <Card.Title
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '16px',
                        cursor: 'text',
                      }}
                    >
                      {design.name}
                    </Card.Title>
                  </Card.Body>
                </Card>
              </Col>
            ))}

            <Modal
              show={show}
              onHide={handleClose}
              centered
              className="thumbnail-modal"
            >
              <Modal.Body>
                <img src={selectedThumbnail} alt="Selected Thumbnail" />
              </Modal.Body>

              <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>

            <Pagination
              pages={pages}
              page={Number(page)}
              filterParam={filterParam}
              baseUrl="/admin/designs"
              search={searchQuery}
            />
          </Row>
        )}
      </div>
    </Container>
  );
}
